<template>
  <div class="section-overview ov custom-dashboard" :class="{ 'cd--editing': editing, 'cd--gesture': editing && (!!gesture || dropActive) }">
    <!-- Édition : barre unique (nom + enregistrer), le reste est dans le panneau -->
    <div v-if="editing" class="cd-editbar">
      <span class="cd-edit-tag"><i class="material-icons">edit</i>{{ $t('dash.overview.dashboards.editing') }}</span>
      <input
        ref="nameInput"
        v-model="draft.name"
        class="cd-name-input"
        maxlength="60"
        :placeholder="$t('dash.overview.dashboards.name_placeholder')"
        @blur="restoreNameIfEmpty"
        @keydown.enter="$event.target.blur()"
      />
      <div class="cd-actions">
        <span v-if="saveError" class="cd-error">{{ $t('dash.overview.dashboards.save_error') }}</span>
        <Tooltip v-if="hasFittable" :text="$t('dash.overview.dashboards.fit_all_help')" position="bottom">
          <button class="ov-action ov-action--ghost" @click="fitAll">
            <i class="material-icons">unfold_less</i><span>{{ $t('dash.overview.dashboards.fit_all') }}</span>
          </button>
        </Tooltip>
        <button class="ov-action ov-action--ghost" @click="cancelEdit">{{ $t('dash.overview.dashboards.cancel') }}</button>
        <button class="ov-action ov-action--primary" :disabled="saving || !draft.name.trim()" @click="save">
          <i class="material-icons" :class="{ spin: saving }">{{ saving ? 'sync' : 'check' }}</i>
          <span>{{ $t('dash.overview.dashboards.save') }}</span>
        </button>
      </div>
    </div>

    <!-- En-tête : nom, visibilité, période, actions -->
    <div v-else class="cd-header">
      <div class="cd-heading">
        <DashboardSwitcher :current-id="isNew || !dashboard ? null : dashboard.id" :current-label="isNew ? draft.name : (dashboard && dashboard.name) || ''" />
        <div class="cd-name-row">
          <h1 class="ov-title cd-name">{{ (dashboard && dashboard.name) || '' }}</h1>
          <Tooltip :text="$t('dash.overview.beta_help')" position="bottom"><span class="ov-beta">{{ $t('dash.overview.beta') }}</span></Tooltip>
          <Tooltip v-if="current" :text="$t(`dash.overview.dashboards.visibility_help.${current.visibility}`)" position="bottom">
            <span class="cd-badge" :class="`cd-badge--${current.visibility}`">
              <i class="material-icons">{{ current.visibility === 'shared' ? 'people' : 'lock_outline' }}</i>
              {{ $t(`dash.overview.dashboards.visibility.${current.visibility}`) }}
            </span>
          </Tooltip>
          <span v-if="dashboard && !dashboard.mine && dashboard.owner_name" class="cd-owner">{{ $t('dash.overview.dashboards.by', { name: dashboard.owner_name }) }}</span>
        </div>
        <DashboardPagesList :pages="scopedPages" :all-pages="!scopePageIds" :per-page="overview ? overview.per_page : null" />
      </div>

      <div class="cd-actions">
        <template v-if="dashboard">
          <Tooltip v-if="dashboard.can_edit" :text="$t('dash.overview.dashboards.help.edit')" position="bottom">
            <button class="ov-action ov-action--primary" @click="startEdit">
              <i class="material-icons">edit</i><span>{{ $t('dash.overview.dashboards.edit') }}</span>
            </button>
          </Tooltip>
          <Tooltip :text="$t('dash.overview.dashboards.help.duplicate')" position="bottom">
            <button class="ov-action" :disabled="duplicating" @click="duplicate">
              <i class="material-icons">content_copy</i><span>{{ $t('dash.overview.dashboards.duplicate') }}</span>
            </button>
          </Tooltip>
          <Tooltip v-if="dashboard.can_edit" :text="$t('dash.overview.dashboards.help.delete')" position="bottom">
            <button class="ov-icon-btn ov-icon-btn--danger" @click="confirmDelete">
              <i class="material-icons">delete</i>
            </button>
          </Tooltip>
        </template>
      </div>
    </div>

    <p v-if="loadError" class="ov-error">
      {{ loadError === 'forbidden' ? $t('dash.overview.dashboards.forbidden') : $t('dash.overview.error') }}
      <button class="ov-arrow-link" @click="goGeneral">{{ $t('dash.overview.dashboards.back_general') }}</button>
    </p>

    <template v-else-if="current">
      <!-- Période : par défaut celle du tableau de bord, modifiable sans l'enregistrer -->
      <div v-if="!editing" class="cd-period">
        <div class="ov-block-title">
          <i class="material-icons ov-title-icon">date_range</i>
          <Tooltip :text="$t('dash.overview.dashboards.help.period')" position="top">
            <h2 class="ov-help-title">{{ $t('dash.overview.dashboards.period') }}<i class="material-icons ov-help-icon">info_outline</i></h2>
          </Tooltip>
          <span v-if="periodChanged" class="ov-chip-muted">{{ $t('dash.overview.dashboards.temporary_period') }}</span>
        </div>
        <OverviewPeriodBar
          :period="period"
          :loading="overviewLoading"
          :updating="overviewLoading && !!overview"
          @select="selectPeriod"
          @refresh="refreshAll"
        />
      </div>

      <OverviewReportingHint
        v-if="!editing && reportingAllowed && overview && !overviewLoading && needs.has('overview')"
        :per-page="overview.per_page"
        :pages="pagesData"
        @open="openReportingForPages"
      />

      <div class="cd-body">
        <!-- Grille -->
        <div
          ref="gridZone"
          class="cd-grid-zone"
          :class="{ 'cd-grid-zone--drop': dropActive }"
          @dragover.prevent="onCatalogDragOver"
          @dragleave="dropActive = false"
          @drop.prevent="onCatalogDrop"
        >
          <div v-if="!layout.length" class="cd-empty">
            <i class="material-icons">widgets</i>
            <p>{{ $t('dash.overview.dashboards.empty') }}</p>
            <button v-if="canEditCurrent" class="ov-action ov-action--primary" @click="startEdit('widgets')">
              <i class="material-icons">add</i><span>{{ $t('dash.overview.dashboards.add_widgets') }}</span>
            </button>
          </div>

          <!-- Petit écran : une colonne, dans l'ordre visuel -->
          <div v-else-if="narrow" class="cd-stack">
            <div v-for="item in stackLayout" :key="item.i" class="cd-stack-item" :style="{ height: `${Math.max(item.h, 4) * 40}px` }">
              <ModuleFrame v-bind="frameProps(item)">
                <component :is="binding(item).is" v-bind="binding(item).props" v-on="binding(item).on" />
              </ModuleFrame>
            </div>
          </div>

          <!-- Grille 12 colonnes : pendant un geste, les widgets sont affichés à leur place finale (aperçu = résultat) -->
          <div v-else ref="grid" class="cd-grid" :style="{ height: `${gridHeight}px` }">
            <div v-if="placeholderBox" class="cd-placeholder" :style="boxStyle(placeholderBox)"></div>
            <div
              v-for="item in displayLayout"
              :key="item.i"
              class="cd-item"
              :class="{ 'cd-item--active': isActiveItem(item), 'cd-item--animate': !isActiveItem(item) }"
              :style="itemStyle(item)"
            >
              <ModuleFrame
                v-bind="frameProps(item)"
                :data-i="item.i"
                @settings="selectWidget(item)"
                @remove="removeWidget(item)"
                @fit="fitItem(item.i)"
                @drag-start="event => startGesture(item, 'move', event)"
                @resize-start="({ edge, event }) => startGesture(item, edge, event)"
              >
                <component :is="binding(item).is" v-bind="binding(item).props" v-on="binding(item).on" />
              </ModuleFrame>
            </div>
          </div>
        </div>

        <!-- Panneau d'édition : widgets, réglages du tableau de bord, options du widget sélectionné -->
        <DashboardEditorPanel
          v-if="editing"
          :tab.sync="panelTab"
          :draft="draft"
          :pages="pagesData"
          :selected="selectedItem"
          :allowed="allowedFlags"
          :range="range"
          :scope-page-ids="scopePageIds"
          @add="addWidget"
          @catalog-drag="key => (catalogDragKey = key)"
          @update-item="updateItem"
          @update="updateDraft"
          @deselect="selectedId = null"
        />
      </div>
    </template>

    <div v-else class="cd-loading">
      <div v-for="n in 3" :key="n" class="cd-loading-block"></div>
    </div>
  </div>
</template>

<script>
import { EventBus } from '@/event-bus'
import Tooltip from '../tooltip/tooltipInit.vue'
import DataLoaders from '../overview/dataLoaders'
import { readPeriod, writePeriod, readSection, writeSection } from '../overview/cache'
import { DEFAULT_PERIOD, periodRange } from '../overview/periods'
import OverviewPeriodBar from '../overview/OverviewPeriodBar.vue'
import DashboardSwitcher from '../overview/modules/DashboardSwitcher.vue'
import DashboardsApi from '../overview/modules/dashboardsApi'
import { setLastDashboard } from '../overview/modules/lastDashboard'
import ModuleFrame from '../overview/modules/ModuleFrame.vue'
import DashboardPagesList from '../overview/modules/DashboardPagesList.vue'
import DashboardEditorPanel from '../overview/modules/DashboardEditorPanel.vue'
import { WIDGETS_BY_KEY, STARTER_LAYOUT, createItem, newItemId } from '../overview/modules/registry'
import { placeMoved, applyResize, resizeTo, addItem, removeItem, compact, readingOrder, COLS } from '../overview/modules/layoutEngine'
import { metricLabel } from '../overview/modules/widgets/metricLabels'
import { isReportingEligible } from '@/utils/reportingEligibility'
import OverviewKpis from '../overview/OverviewKpis.vue'
import OverviewReportingHint from '../overview/OverviewReportingHint.vue'
import OverviewAlerts from '../overview/OverviewAlerts.vue'
import OverviewPages from '../overview/OverviewPages.vue'
import OverviewUpcoming from '../overview/OverviewUpcoming.vue'
import OverviewTopPosts from '../overview/OverviewTopPosts.vue'
import OverviewLastPosts from '../overview/OverviewLastPosts.vue'
import OverviewBestTimes from '../overview/OverviewBestTimes.vue'
import OverviewNetworks from '../overview/OverviewNetworks.vue'
import OverviewSources from '../overview/OverviewSources.vue'
import OverviewPresence from '../overview/OverviewPresence.vue'
import MetricWidget from '../overview/modules/widgets/MetricWidget.vue'
import ShortLinksWidget from '../overview/modules/widgets/ShortLinksWidget.vue'
import { shortLinksAvailable } from '@/utils/shortLinkMetrics'
import ReportingPanelWidget from '../overview/modules/widgets/ReportingPanelWidget.vue'
import CalendarWidget from '../overview/modules/widgets/CalendarWidget.vue'
import DistributionWidget from '../overview/modules/widgets/DistributionWidget.vue'
import InboxWidget from '../overview/modules/widgets/InboxWidget.vue'
import NoteWidget from '../overview/modules/widgets/NoteWidget.vue'
import ActionsWidget from '../overview/modules/widgets/ActionsWidget.vue'
import { NOTE_BACKGROUNDS, NOTE_TEXT_COLORS, resolveColor } from '../overview/modules/widgets/noteColors'
import { DASHBOARD_ACTIONS } from '../overview/modules/dashboardActions'

const { VUE_APP_API_HOST } = process.env

const NARROW = 900
const ROW_HEIGHT = 40
const MARGIN = 16

/**
 * Tableau de bord personnalisé : pages, widgets et disposition choisis par l'utilisateur.
 * Lecture : grille figée ; édition : déplacement / redimensionnement, catalogue et réglages, enregistrés à la validation.
 */
export default {
  name: 'custom-dashboard',

  components: {
    DashboardPagesList,
    OverviewReportingHint,
    Tooltip,
    OverviewPeriodBar,
    DashboardSwitcher,
    ModuleFrame,
    DashboardEditorPanel
  },

  mixins: [DataLoaders, DashboardsApi],

  data() {
    return {
      dashboard: null,
      loadError: null,
      period: { ...DEFAULT_PERIOD },
      editing: false,
      draft: { name: '', visibility: 'private', pages: [], period: {}, layout: [] },
      lastValidName: '',
      saving: false,
      saveError: false,
      duplicating: false,
      panelTab: 'widgets',
      selectedId: null,
      refreshToken: 0,
      narrow: false,
      dropActive: false,
      catalogDragKey: null,
      // Geste en cours (déplacement ou redimensionnement) : layout de départ, aperçu, position du widget tiré
      gesture: null,
      // Largeur de la grille (px) : colonnes calculées comme vue-grid-layout (marges de 16 px)
      gridWidth: 0,
      // Ajustements de hauteur en attente, par widget (jeton : le dernier demandé l'emporte)
      fitTokens: {},
      // Données de l'overview des widgets en focus sur certaines pages, par ensemble de pages (« id1,id2 »)
      scopedOverviews: {}
    }
  },

  computed: {
    dashboardId() {
      return this.$route.params.id
    },

    isNew() {
      return this.dashboardId === 'new'
    },

    // Données affichées : brouillon en édition, sinon tableau de bord enregistré
    current() {
      return this.editing ? this.draft : this.dashboard
    },

    layout() {
      return (this.current && this.current.layout) || []
    },

    stackLayout() {
      return readingOrder(this.layout)
    },

    canEditCurrent() {
      return this.isNew || !!(this.dashboard && this.dashboard.can_edit)
    },

    // Aucune page choisie = toutes les pages accessibles
    scopePageIds() {
      const pages = this.current && this.current.pages
      return pages && pages.length ? pages.map(String) : null
    },

    cacheScope() {
      return `dashboard:${this.dashboardId}`
    },

    range() {
      return periodRange(this.period)
    },

    savedPeriod() {
      const p = this.current && this.current.period
      return p && p.preset ? { key: p.preset } : (p && p.since && p.until ? { key: 'custom', start: p.since, end: p.until } : { ...DEFAULT_PERIOD })
    },

    periodChanged() {
      return JSON.stringify(this.period) !== JSON.stringify(this.savedPeriod)
    },

    needs() {
      const set = new Set()
      this.layout.forEach(item => (this.defOf(item).needs || []).forEach(n => set.add(n)))
      return set
    },

    // Ensembles de pages propres à des widgets basés sur l'overview
    customScopes() {
      const keys = new Set()
      this.layout.forEach(item => {
        const ids = this.itemPageIds(item)
        if (ids && (this.defOf(item).needs || []).includes('overview')) keys.add(ids.join(','))
      })
      return [...keys]
    },

    hasFittable() {
      return this.editing && this.layout.some(item => this.isFittable(item))
    },

    colWidth() {
      return Math.max(0, (this.gridWidth - MARGIN * (COLS + 1)) / COLS)
    },

    // Pendant un geste : la disposition qu'on obtiendra en relâchant
    displayLayout() {
      return this.gesture && this.gesture.preview ? this.gesture.preview : this.layout
    },

    // Une ligne de plus en édition : on peut déposer sous le dernier widget
    gridHeight() {
      const rows = this.displayLayout.reduce((max, item) => Math.max(max, item.y + item.h), 0) + (this.editing ? 2 : 0)
      return rows * (ROW_HEIGHT + MARGIN) + MARGIN
    },

    // Emplacement final du widget déplacé
    placeholderBox() {
      const g = this.gesture
      if (!g || g.kind !== 'move' || !g.moved || !g.preview) return null
      const item = g.preview.find(it => it.i === g.id)
      return item ? this.box(item) : null
    },

    selectedItem() {
      return this.layout.find(item => item.i === this.selectedId) || null
    },

    // Widget « Clics sur les liens » : raccourcisseur ouvert au compte (feature beta url_shortener)
    shortLinksAllowed() {
      return shortLinksAvailable()
    },

    allowedFlags() {
      return { reporting: this.reportingAllowed, inbox: this.inboxAllowed, presence: this.presenceEnabled, shortLinks: this.shortLinksAllowed }
    }
  },

  watch: {
    dashboardId(value) {
      if (this.dashboard && this.dashboard.slug === value) return
      this.reset()
      this.init()
    },

    // Pages modifiées dans les réglages : données rechargées pour le nouveau périmètre
    scopePageIds(value, old) {
      if (JSON.stringify(value) === JSON.stringify(old) || !this.current) return
      this.reloadData()
    },

    needs(value, old) {
      if ([...value].some(n => !old.has(n))) this.reloadData()
    },

    // Nouveau focus de pages : chargé tout de suite
    customScopes(value, old) {
      value.filter(key => !old.includes(key) && !this.scopedOverviews[key]).forEach(key => this.loadScope(key))
    }
  },

  beforeMount() {
    this.$ga.page(this.$router)
  },

  mounted() {
    localStorage.setItem('section', 'overview')
    this.onWindowResize()
    window.addEventListener('resize', this.onWindowResize)
    this.gridObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => this.measureGrid()) : null
    this.init()
  },

  // La grille n'existe qu'une fois le tableau de bord chargé (et hors petit écran) : observée dès qu'elle apparaît
  updated() {
    const grid = this.$refs.grid || null
    if (grid !== this.observedGrid) {
      if (this.gridObserver) this.gridObserver.disconnect()
      if (grid && this.gridObserver) this.gridObserver.observe(grid)
      this.observedGrid = grid
    }
    this.measureGrid()
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize)
    if (this.gridObserver) this.gridObserver.disconnect()
    this.onGestureEnd()
  },

  beforeRouteLeave(to, from, next) {
    if (!this.editing || !this.isDirty()) return next()
    EventBus.$emit('showModal', this.$t('dash.overview.dashboards.leave_title'), this.$t('dash.overview.dashboards.leave_text'),
      this.$t('dash.overview.dashboards.leave_confirm'), this.$t('dash.overview.dashboards.cancel'), () => next(), () => next(false), null, true)
  },

  methods: {
    reset() {
      this.scopedOverviews = {}
      this.dashboard = null
      this.loadError = null
      this.editing = false
      this.selectedId = null
      this.overview = null
      this.upcoming = []
      this.riskCandidates = []
      this.validationPosts = []
      this.assignedPosts = []
      this.inboxEvents = null
      this.inboxEventsFresh = false
      this.lastPosts = null
      this.lastPostStats = {}
    },

    async init() {
      if (this.isNew) {
        const name = this.$route.params.defaultName || this.defaultDashboardName(this.cachedDashboards() || [])
        this.dashboard = null
        this.draft = {
          name,
          visibility: 'private',
          pages: [],
          period: { preset: DEFAULT_PERIOD.key },
          layout: compact(STARTER_LAYOUT.map(item => ({ ...createItem(item.module), ...item, i: newItemId() })))
        }
        this.lastValidName = name
        this.period = { ...DEFAULT_PERIOD }
        this.editing = true
        this.panelTab = 'widgets'
        this.$nextTick(() => this.focusName())
        await this.loadPages()
        this.reloadData()
        return
      }

      // Définition en cache affichée tout de suite, rafraîchie ensuite
      const cached = readSection(this.cacheUserId(), `dashboard:${this.dashboardId}`)
      if (cached) this.applyDashboard(cached)
      if (this.dashboard) this.hydrateFromCache()
      this.loadPages().then(() => this.alive && this.dashboard && this.reloadData(false))
      try {
        const dashboard = await this.fetchDashboard(this.dashboardId)
        if (!this.alive) return
        const changed = !this.dashboard || JSON.stringify(dashboard) !== JSON.stringify(this.dashboard)
        this.applyDashboard(dashboard)
        writeSection(this.cacheUserId(), `dashboard:${this.dashboardId}`, dashboard)
        if (changed) {
          this.hydrateFromCache()
          this.reloadData()
        }
      } catch (e) {
        if (!this.alive) return
        writeSection(this.cacheUserId(), `dashboard:${this.dashboardId}`, null)
        this.dashboard = null
        this.loadError = e && (e.status === 403 || e.status === 404) ? 'forbidden' : 'error'
        if (this.loadError === 'forbidden') setLastDashboard(null)
      }
    },

    applyDashboard(dashboard) {
      // Widgets retirés du catalogue (ex. comparaison des pages) : ignorés, ils disparaissent au prochain enregistrement
      this.dashboard = dashboard ? { ...dashboard, layout: (dashboard.layout || []).filter(item => WIDGETS_BY_KEY[item.module]) } : dashboard
      if (dashboard && dashboard.slug) setLastDashboard(dashboard.slug)
      this.period = { ...this.savedPeriod }
      this.scheduleGrowTexts()
    },

    // Widgets Texte : jamais de défilement, la case grandit si le texte dépasse (largeur d'écran, retours à la ligne, polices)
    growTexts() {
      if (this.narrow || this.gesture) return
      const source = this.editing ? this.draft.layout : (this.dashboard && this.dashboard.layout)
      if (!source) return
      let layout = source
      source.filter(item => item.module === 'note').forEach(item => {
        const h = this.fitHeight(layout.find(it => it.i === item.i))
        if (h && h > item.h) layout = applyResize(layout, item.i, item.w, h, WIDGETS_BY_KEY)
      })
      if (layout === source) return
      if (this.editing) this.draft.layout = layout
      else this.dashboard = { ...this.dashboard, layout }
    },

    scheduleGrowTexts() {
      clearTimeout(this.growTimer)
      // Après le rendu et le chargement des polices
      this.growTimer = setTimeout(() => { if (this.alive) this.$nextTick(() => this.growTexts()) }, 300)
    },

    // Chargements limités aux besoins des widgets présents
    reloadData(withPages = false) {
      if (withPages) this.loadPages()
      const cached = readPeriod(this.cacheKey(), this.period)
      if (cached) this.overview = cached
      // Retour du Reporting (encart) : calcul relancé sans le cache de l'engine
      const refresh = this.consumeReportingRefresh()
      if (this.needs.has('overview') || this.needs.has('pages')) this.loadOverview(refresh)
      this.loadScopes(refresh)
      if (this.needs.has('posts') || this.needs.has('pages')) this.loadPostsAlerts()
      if (this.needs.has('inbox')) {
        this.loadInboxCount()
        this.inboxEventsFresh = false
        if (this.layout.some(item => item.module === 'inbox')) this.loadInboxEvents()
      }
    },

    // Boutons d'action du widget, dans l'ordre choisi, limités aux droits de l'utilisateur
    actionsFor(o, ids) {
      const allowed = {
        create: this.canEdit,
        planner: true,
        inbox: this.inboxAllowed,
        reporting: this.reportingAllowed,
        pages: this.canEdit,
        sources: this.canEdit,
        presence: this.presenceEnabled
      }
      return (o.actions || []).map(key => DASHBOARD_ACTIONS.find(a => a.key === key)).filter(a => a && allowed[a.key]).map(a => ({
        ...a,
        label: this.$t(a.label),
        badge: a.key === 'inbox' && this.inboxAllowed ? (ids ? ids.reduce((sum, id) => sum + (this.inboxCounts[id] || 0), 0) : this.inboxCount) : 0
      }))
    },

    // Pages d'un bouton seul (option du widget), limitées à celles du tableau de bord ; sinon null (pages du tableau de bord)
    actionPageIds(o) {
      const ids = (o.actionPages || []).map(String)
      if (!ids.length || (o.actions || []).length !== 1) return null
      const allowed = new Set(this.scopedPages.map(p => String(p.id)))
      const kept = ids.filter(id => allowed.has(id))
      return kept.length ? kept : null
    },

    // Bouton appliqué à ses pages (celles du bouton, sinon du tableau de bord) ; les autres mènent à leur section
    runAction(action, ids) {
      const pageIds = (ids || this.scopePageIds || []).map(String)
      if (action.key === 'reporting') {
        const eligible = pageIds.filter(id => this.pagesData.some(p => String(p.id) === id && isReportingEligible(p)))
        if (eligible.length) return this.openReportingForPages(eligible)
      }
      if (action.key === 'create' && pageIds.length) return this.go({ name: 'postCreator', params: { pageId: pageIds.join(',') } })
      if (action.key === 'inbox' && pageIds.length === 1) return this.go({ name: 'moderation', params: { moderation: pageIds[0] } })
      // Planner (et Inbox multi-pages) : pages du tableau de bord sélectionnées dans le sélecteur commun
      if (['planner', 'inbox'].includes(action.key) && pageIds.length) {
        this.$store.commit('selectPage/updatePagesSelected', { pagesId: pageIds, saveToLocalStorage: false })
      }
      this.go(action.target)
    },

    // Pages propres au widget (option « Source »), limitées à celles du tableau de bord ; null = toutes
    itemPageIds(item) {
      const ids = (item.options && item.options.pages) || []
      if (!ids.length) return null
      const allowed = new Set(this.scopedPages.map(p => String(p.id)))
      const kept = ids.map(String).filter(id => allowed.has(id)).sort()
      return kept.length ? kept : null
    },

    loadScopes(refresh = false) {
      this.customScopes.forEach(key => this.loadScope(key, refresh))
    },

    // Overview (et dernières publications) d'un ensemble de pages, avec cache local par période
    async loadScope(key, refresh = false) {
      const requestId = ((this.scopedOverviews[key] && this.scopedOverviews[key].requestId) || 0) + 1
      const cacheKey = `${this.cacheKey()}:${key}`
      const previous = this.scopedOverviews[key] || {}
      this.$set(this.scopedOverviews, key, { ...previous, overview: readPeriod(cacheKey, this.period) || previous.overview || null, loading: true, requestId })
      const { since, until } = periodRange(this.period)
      const params = new URLSearchParams()
      if (since !== null) {
        params.set('since', since)
        params.set('until', until)
      }
      params.set('timezone', new Date().getTimezoneOffset())
      params.set('pages', key)
      if (refresh) params.set('refresh', '1')
      const period = this.period
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` }
      const current = () => this.alive && this.scopedOverviews[key] && this.scopedOverviews[key].requestId === requestId
      const wantsLast = this.layout.some(item => item.module === 'last_posts' && (this.itemPageIds(item) || []).join(',') === key)
      if (wantsLast) {
        this.$http.get(`${VUE_APP_API_HOST}/reporting/overview/last-posts?${params.toString()}`, { headers })
          .then(res => current() && this.$set(this.scopedOverviews, key, { ...this.scopedOverviews[key], lastPosts: res.body }))
          .catch(() => {})
      }
      try {
        const res = await this.$http.get(`${VUE_APP_API_HOST}/reporting/overview?${params.toString()}&previous=0`, { headers })
        if (!current()) return
        const overview = this.keepComparisons(res.body, this.scopedOverviews[key].overview)
        writePeriod(cacheKey, period, overview)
        this.$set(this.scopedOverviews, key, { ...this.scopedOverviews[key], overview, loading: false })
        // Comparaisons ajoutées à leur arrivée
        if (res.body.previous_pending) {
          const prev = await this.$http.get(`${VUE_APP_API_HOST}/reporting/overview/previous?${params.toString()}`, { headers })
          if (!current()) return
          const withPrev = this.withComparisons(this.scopedOverviews[key].overview, prev.body)
          writePeriod(cacheKey, period, withPrev)
          this.$set(this.scopedOverviews, key, { ...this.scopedOverviews[key], overview: withPrev })
        }
      } catch (e) {
        if (!current()) return
        console.error('[dashboard] scope', e)
        this.$set(this.scopedOverviews, key, { ...this.scopedOverviews[key], loading: false })
      }
    },

    refreshAll() {
      this.refreshToken++
      if (this.needs.has('overview') || this.needs.has('pages')) this.loadOverview(true)
      this.loadScopes(true)
      if (this.needs.has('posts')) this.loadPostsAlerts()
    },

    selectPeriod(period) {
      this.period = period
      const cached = readPeriod(this.cacheKey(), period)
      if (cached) this.overview = cached
      if (this.needs.has('overview') || this.needs.has('pages')) this.loadOverview()
      this.loadScopes()
      // En édition, la période choisie devient la période par défaut du tableau de bord
      if (this.editing) this.draft.period = period.key === 'custom' ? { since: period.start, until: period.end } : { preset: period.key }
    },

    defOf(item) {
      return WIDGETS_BY_KEY[item.module] || { minW: 2, minH: 2, maxH: 40, needs: [], icon: 'widgets' }
    },

    widgetTitle(item) {
      const options = item.options || {}
      if (options.title) return options.title
      if (item.module === 'metric') {
        const keys = options.keys && options.keys.length ? options.keys : ['impressions']
        return keys.map(k => metricLabel(this, k, options.network)).join(' · ')
      }
      return this.$t(`dash.overview.widgets.${item.module}.title`)
    },

    // Pages d'un panneau Reporting (pageIds, ou pageId des tableaux de bord enregistrés avant le multi-pages)
    panelPages(item) {
      const o = item.options || {}
      const ids = (Array.isArray(o.pageIds) ? o.pageIds : (o.pageId ? [o.pageId] : [])).map(String)
      const pages = ids.map(id => this.pagesData.find(p => String(p.id) === id)).filter(Boolean)
      return this.defOf(item).singlePage ? pages.slice(0, 1) : pages
    },

    // Pages affichées dans l'en-tête du widget (nom + réseau) : pages du panneau, ou focus de la source
    widgetSources(item) {
      if (this.defOf(item).scope === 'single') return this.panelPages(item)
      const ids = this.itemPageIds(item)
      return ids ? ids.map(id => this.pagesData.find(p => String(p.id) === id)).filter(Boolean) : []
    },

    widgetSubtitle(item) {
      if (item.module === 'metric' && item.options && item.options.network) return this.$t(`dash.overview.network.${item.options.network}`)
      return ''
    },

    isLocked(item) {
      const def = this.defOf(item)
      return (def.reporting && !this.reportingAllowed) || (def.inbox && !this.inboxAllowed) || (def.presence && !this.presenceEnabled) || (def.shortLinks && !this.shortLinksAllowed)
    },

    frameProps(item) {
      return {
        def: this.defOf(item),
        title: this.widgetTitle(item),
        subtitle: this.widgetSubtitle(item),
        sources: this.widgetSources(item).map(p => ({ id: String(p.id), name: p.name, network: p.network })),
        help: this.$te(`dash.overview.widgets.${item.module}.help`) ? this.$t(`dash.overview.widgets.${item.module}.help`) : '',
        editing: this.editing,
        hideHead: !!(item.options && item.options.hideTitle),
        icon: (item.options || {}).titleIcon === 'none' ? null : ((item.options || {}).titleIcon || this.defOf(item).icon),
        background: resolveColor((item.options || {}).background, NOTE_BACKGROUNDS),
        titleColor: resolveColor((item.options || {}).titleColor, NOTE_TEXT_COLORS),
        selected: this.editing && this.selectedId === item.i,
        locked: this.isLocked(item)
      }
    },

    // Composant, props et événements de chaque widget
    binding(item) {
      const o = item.options || {}
      const ids = this.itemPageIds(item)
      const scope = ids ? this.scopedOverviews[ids.join(',')] || {} : null
      const overview = scope ? scope.overview || null : this.overview
      const loading = scope ? !overview : this.overviewLoading && !overview
      const idSet = ids ? new Set(ids) : null
      const inScope = list => (idSet && list ? list.filter(x => idSet.has(String(x.page_id))) : list)
      const pagesIn = idSet ? this.scopedPages.filter(p => idSet.has(String(p.id))) : this.scopedPages
      const lastPosts = scope ? scope.lastPosts || (overview && overview.last_posts) || null : this.lastPostsView
      const inboxCount = idSet ? ids.reduce((sum, id) => sum + (this.inboxCounts[id] || 0), 0) : this.inboxCount
      const kpis = parts => ({ is: OverviewKpis, props: { data: overview, loading, reportingAllowed: this.reportingAllowed, isDayGranularity: this.period.key === '24h', parts, tileKeys: o.tiles || null, fill: true }, on: { go: name => this.go({ name }) } })
      const range = this.range
      switch (item.module) {
        case 'kpis': return kpis(['tiles'])
        case 'chart': return kpis(['chart'])
        case 'networks': return { is: OverviewNetworks, props: { perPage: overview ? overview.per_page : null, pages: this.pagesData, loading }, on: {} }
        case 'best_times': return { is: OverviewBestTimes, props: { grid: overview ? overview.best_times : null, loading, fill: true }, on: {} }
        case 'calendar': return { is: CalendarWidget, props: { options: o, series: overview ? overview.series : null, loading }, on: {} }
        case 'distribution': return { is: DistributionWidget, props: { publishing: overview ? overview.publishing : null, loading }, on: {} }
        case 'short_links': return { is: ShortLinksWidget, props: { options: o, pageIds: ids || this.scopePageIds, pages: this.pagesData, range, refreshToken: this.refreshToken }, on: {} }
        case 'metric': return { is: MetricWidget, props: { options: o, pageIds: ids || this.scopePageIds, pages: this.pagesData, range, refreshToken: this.refreshToken }, on: {} }
        case 'last_posts': return { is: OverviewLastPosts, props: { last: lastPosts, lastStats: this.lastPostStats, loading: !lastPosts && (scope ? scope.loading !== false : this.lastPostsLoading || this.overviewLoading) }, on: { go: this.go, 'open-last-post': this.openPublishedPost, 'open-network': this.openOnNetwork } }
        case 'top_posts': return { is: OverviewTopPosts, props: { top: overview ? overview.top_posts : null, loading }, on: { 'open-post': this.openPublishedPost } }
        case 'upcoming': return { is: OverviewUpcoming, props: { items: inScope(this.upcomingView), loading: this.upcomingLoading && !this.upcoming.length, canEdit: this.canEdit }, on: { go: this.go, 'open-post': this.openPublishedPost } }
        case 'alerts': return {
          is: OverviewAlerts,
          props: {
            pages: pagesIn, riskPosts: inScope(this.riskPosts), validationPosts: inScope(this.validationPosts), assignedPosts: inScope(this.assignedPosts),
            inboxCount: this.inboxAllowed ? inboxCount : 0, inboxEvents: inScope(this.inboxEvents), inboxEventsLoading: this.inboxEventsLoading,
            canEdit: this.canEdit, canReconnectInline: this.canReconnectInline, loading: !this.sideLoaded
          },
          on: { 'load-inbox': this.loadInboxEvents, 'open-inbox-event': this.openInboxEvent, reconnect: this.reconnect, go: this.go, 'open-post': this.openPublishedPost }
        }
        case 'pages': return {
          is: OverviewPages,
          props: {
            pages: pagesIn, perPage: overview ? overview.per_page : null, canEdit: this.canEdit, reportingAllowed: this.reportingAllowed,
            canReconnectInline: this.canReconnectInline, inboxCounts: this.inboxAllowed ? this.inboxCounts : {}, nextScheduled: this.nextScheduledByPage
          },
          on: { reconnect: this.reconnect, 'native-reconnected': this.onNativeReconnected, go: this.go, 'create-post': this.createPostForPage, 'open-reporting': this.openReportingForPage, 'open-inbox': this.openInboxForPage }
        }
        case 'inbox': return { is: InboxWidget, props: { events: inScope(this.inboxEvents), loading: this.inboxEventsLoading }, on: { 'load-inbox': this.loadInboxEvents, 'open-inbox-event': this.openInboxEvent } }
        case 'sources': return { is: OverviewSources, props: { userId: this.cacheUserId(), canEdit: this.canEdit }, on: { go: this.go, 'open-post': this.openPublishedPost, 'open-network': this.openOnNetwork } }
        case 'presence': return { is: OverviewPresence, props: { userId: this.cacheUserId() }, on: { go: this.go } }
        case 'note': return { is: NoteWidget, props: { options: o }, on: {} }
        case 'actions': return { is: ActionsWidget, props: { actions: this.actionsFor(o, this.actionPageIds(o)), layout: o.layout || 'row', align: o.align || 'center' }, on: { action: action => this.runAction(action, this.actionPageIds(o)) } }
        default: {
          const def = this.defOf(item)
          if (def.component) {
            return { is: ReportingPanelWidget, props: { panel: def.component, pages: this.panelPages(item), range, refreshToken: this.refreshToken }, on: {} }
          }
          return { is: NoteWidget, props: { options: {} }, on: {} }
        }
      }
    },

    // --- Édition ---

    startEdit(tab) {
      if (!this.canEditCurrent || !this.dashboard) return
      this.draft = JSON.parse(JSON.stringify({
        name: this.dashboard.name,
        visibility: this.dashboard.visibility,
        pages: this.dashboard.pages || [],
        period: this.dashboard.period || {},
        layout: this.dashboard.layout || []
      }))
      this.lastValidName = this.draft.name
      this.panelTab = typeof tab === 'string' ? tab : 'widgets'
      this.selectedId = null
      this.saveError = false
      this.editing = true
    },

    isDirty() {
      if (this.isNew) return true
      const d = this.dashboard || {}
      return JSON.stringify(this.draft) !== JSON.stringify({ name: d.name, visibility: d.visibility, pages: d.pages || [], period: d.period || {}, layout: d.layout || [] })
    },

    cancelEdit() {
      if (this.isNew) {
        this.editing = false
        this.goGeneral()
        return
      }
      this.editing = false
      this.selectedId = null
      this.period = { ...this.savedPeriod }
    },

    focusName() {
      const input = this.$refs.nameInput
      if (!input) return
      input.focus()
      input.select()
    },

    restoreNameIfEmpty() {
      if (!this.draft.name.trim()) this.draft.name = this.lastValidName
      else this.lastValidName = this.draft.name.trim()
    },

    updateDraft({ field, value }) {
      this.draft = { ...this.draft, [field]: value }
      if (field === 'period') this.period = value.preset ? { key: value.preset } : { ...DEFAULT_PERIOD }
    },

    updateItem({ i, options }) {
      const before = this.draft.layout.find(item => item.i === i)
      this.draft.layout = this.draft.layout.map(item => (item.i === i ? { ...item, options } : item))
      // Contenu différent (autre page, autre visualisation, autres métriques) : hauteur réajustée
      const changed = before && ['pageId', 'pageIds', 'viz', 'keys', 'tiles', 'text', 'size', 'align', 'hideTitle', 'titleIcon', 'actions', 'layout'].some(k => JSON.stringify((before.options || {})[k]) !== JSON.stringify(options[k]))
      if (changed) this.scheduleFit(i)
    },

    // Hauteur naturelle du widget (px) : lue en hauteur automatique le temps d'un reflow ; null pendant un chargement
    contentHeight(i) {
      const frame = this.$refs.gridZone && this.$refs.gridZone.querySelector(`.mf[data-i="${i}"]`)
      if (!frame || frame.querySelector('[class*="skeleton"]')) return null
      frame.classList.add('mf--measure')
      const height = frame.offsetHeight
      frame.classList.remove('mf--measure')
      return height
    },

    // Ajuste la hauteur du widget à son contenu (en lignes de grille, dans ses bornes)
    // Widget dont la hauteur suit le contenu (Métrique : tuiles et classement seulement, courbes et donuts remplissent la case)
    isFittable(item) {
      if (!this.defOf(item).fit) return false
      return item.module !== 'metric' || ['tile', 'ranking'].includes((item.options || {}).viz || 'tile')
    },

    // Hauteur cible en lignes de grille (dans les bornes du widget) ; null si pas mesurable (chargement)
    fitHeight(item) {
      if (!this.isFittable(item)) return null
      const px = this.contentHeight(item.i)
      if (!px) return null
      const def = this.defOf(item)
      return Math.max(def.minH || 1, Math.min(def.maxH || 40, Math.ceil((px + MARGIN) / (ROW_HEIGHT + MARGIN))))
    },

    fitItem(i) {
      const item = this.draft.layout.find(it => it.i === i)
      const h = item && this.fitHeight(item)
      if (!h) return false
      if (h === item.h) return true
      this.draft.layout = applyResize(this.draft.layout, i, item.w, h, WIDGETS_BY_KEY)
      return true
    },

    // Tous les widgets ajustables : mesurés d'abord, puis redimensionnés en une fois (un seul rendu de la grille)
    fitAll() {
      const targets = this.draft.layout.map(item => ({ item, h: this.fitHeight(item) })).filter(t => t.h && t.h !== t.item.h)
      if (!targets.length) return
      let layout = this.draft.layout
      targets.forEach(({ item, h }) => { layout = applyResize(layout, item.i, item.w, h, WIDGETS_BY_KEY) })
      this.draft.layout = layout
    },

    // Après ajout ou changement de contenu : plusieurs essais, le temps que les données arrivent
    scheduleFit(i) {
      const item = this.draft.layout.find(it => it.i === i)
      if (!item || !this.defOf(item).fit) return
      const token = (this.fitTokens[i] || 0) + 1
      this.fitTokens[i] = token
      ;[700, 1800, 4000, 8000].forEach(delay => setTimeout(() => {
        if (this.alive && this.editing && this.fitTokens[i] === token) this.fitItem(i)
      }, delay))
    },

    selectWidget(item) {
      this.selectedId = item.i
      this.panelTab = 'widget'
    },

    addWidget(key, pointer) {
      const item = createItem(key, this.defaultOptionsFor(key))
      let layout = addItem(this.draft.layout, item, WIDGETS_BY_KEY)
      // Déposé sur la grille : placé à l'endroit visé (avec rééquilibrage de la ligne)
      if (pointer) {
        const added = layout.find(it => it.i === item.i)
        layout = placeMoved(layout, item.i, Math.min(pointer.col, COLS - added.w), pointer.row, WIDGETS_BY_KEY, pointer, { isNew: true })
      }
      this.draft.layout = layout
      this.scheduleFit(item.i)
      this.selectedId = item.i
      if (this.defOf(item).scope === 'single' || key === 'metric' || key === 'note') this.panelTab = 'widget'
    },

    // Widgets « une page » : première page éligible au Reporting (hors groupes, conciergerie seule…) sur un réseau du widget
    defaultOptionsFor(key) {
      const def = WIDGETS_BY_KEY[key]
      if (def.scope !== 'single') return {}
      const page = this.scopedPages.find(p => isReportingEligible(p) && (!def.networks || def.networks.includes(p.network)))
      return { options: { ...(def.options || {}), pageIds: page ? [String(page.id)] : [] } }
    },

    removeWidget(item) {
      this.draft.layout = removeItem(this.draft.layout, item.i)
      if (this.selectedId === item.i) this.selectedId = null
    },

    // Gestes sur la grille : snapshot au premier événement, disposition recalculée au relâchement
    // --- Grille : géométrie (mêmes règles que vue-grid-layout : 12 colonnes, lignes de 40 px, marges de 16 px)

    box(item) {
      const col = this.colWidth
      return {
        left: MARGIN + item.x * (col + MARGIN),
        top: MARGIN + item.y * (ROW_HEIGHT + MARGIN),
        width: item.w * col + (item.w - 1) * MARGIN,
        height: item.h * ROW_HEIGHT + (item.h - 1) * MARGIN
      }
    },

    boxStyle(box) {
      return { left: `${box.left}px`, top: `${box.top}px`, width: `${box.width}px`, height: `${box.height}px` }
    },

    isActiveItem(item) {
      return !!(this.gesture && this.gesture.moved && this.gesture.id === item.i)
    },

    // Le widget déplacé suit le curseur ; les autres sont à leur place dans l'aperçu
    itemStyle(item) {
      const g = this.gesture
      if (g && g.kind === 'move' && g.moved && g.id === item.i) return this.boxStyle(g.dragBox)
      return this.boxStyle(this.box(item))
    },

    measureGrid() {
      const grid = this.$refs.grid
      if (grid && grid.clientWidth && grid.clientWidth !== this.gridWidth) {
        this.gridWidth = grid.clientWidth
        // Largeur changée : les textes peuvent passer sur plus de lignes
        this.scheduleGrowTexts()
      }
    },

    // --- Gestes : déplacement (move) ou redimensionnement par un bord / coin (n, s, e, w, ne, nw, se, sw)

    startGesture(item, kind, event) {
      if (!this.editing || this.gesture) return
      event.preventDefault()
      this.measureGrid()
      const snapshot = JSON.parse(JSON.stringify(this.draft.layout))
      const start = snapshot.find(it => it.i === item.i)
      const grid = this.$refs.grid
      if (!start || !grid) return
      // Positions relatives à la grille : le widget reste sous le curseur même si la page défile pendant le geste
      const rect = grid.getBoundingClientRect()
      this.gesture = {
        kind, id: item.i, snapshot, start,
        origin: { x: event.clientX - rect.left, y: event.clientY - rect.top },
        point: { x: event.clientX, y: event.clientY },
        startBox: this.box(start), dragBox: this.box(start), moved: false, preview: null, key: ''
      }
      this.scroller = this.scrollParent(grid)
      window.addEventListener('pointermove', this.onGestureMove)
      window.addEventListener('pointerup', this.onGestureEnd)
      window.addEventListener('pointercancel', this.onGestureEnd)
      window.addEventListener('scroll', this.applyGesture, true)
      this.autoScrollFrame = requestAnimationFrame(this.autoScroll)
    },

    onGestureMove(event) {
      if (!this.gesture) return
      this.gesture.point = { x: event.clientX, y: event.clientY }
      this.applyGesture()
    },

    // Conteneur qui défile (section du tableau de bord ou page)
    scrollParent(el) {
      for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
        const style = getComputedStyle(node)
        if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) return node
      }
      return document.scrollingElement || document.documentElement
    },

    // Défilement automatique près du haut / du bas de la zone visible, plus rapide au bord
    autoScroll() {
      const g = this.gesture
      if (!g) return
      if (g.moved && this.scroller) {
        const isPage = this.scroller === document.scrollingElement || this.scroller === document.documentElement
        const top = isPage ? 0 : this.scroller.getBoundingClientRect().top
        const bottom = isPage ? window.innerHeight : this.scroller.getBoundingClientRect().bottom
        const zone = 80
        let speed = 0
        if (g.point.y < top + zone) speed = -Math.ceil((top + zone - g.point.y) / 4)
        else if (g.point.y > bottom - zone) speed = Math.ceil((g.point.y - (bottom - zone)) / 4)
        if (speed) this.scroller.scrollTop += Math.max(-24, Math.min(24, speed))
      }
      this.autoScrollFrame = requestAnimationFrame(this.autoScroll)
    },

    applyGesture() {
      const g = this.gesture
      const grid = this.$refs.grid
      if (!g || !grid) return
      const rect = grid.getBoundingClientRect()
      const event = { clientX: g.point.x, clientY: g.point.y }
      const dx = event.clientX - rect.left - g.origin.x
      const dy = event.clientY - rect.top - g.origin.y
      if (!g.moved && Math.hypot(dx, dy) < 4) return
      g.moved = true
      const stepX = this.colWidth + MARGIN
      const stepY = ROW_HEIGHT + MARGIN
      let key
      let compute
      if (g.kind === 'move') {
        g.dragBox = { ...g.startBox, left: g.startBox.left + dx, top: g.startBox.top + dy }
        const x = Math.max(0, Math.min(COLS - g.start.w, Math.round((g.dragBox.left - MARGIN) / stepX)))
        const y = Math.max(0, Math.round((g.dragBox.top - MARGIN) / stepY))
        const cell = this.pointerCell({ x: event.clientX, y: event.clientY })
        key = [x, y, cell && cell.col, cell && cell.row].join(':')
        compute = () => placeMoved(g.snapshot, g.id, x, y, WIDGETS_BY_KEY, cell)
      } else {
        const def = this.defOf(g.start)
        const minW = def.minW || 2
        const minH = def.minH || 2
        const maxH = def.maxH || 40
        const dc = Math.round(dx / stepX)
        const dr = Math.round(dy / stepY)
        const rect = { x: g.start.x, w: g.start.w, h: g.start.h }
        if (g.kind.includes('e')) rect.w = Math.max(minW, Math.min(COLS - g.start.x, g.start.w + dc))
        if (g.kind.includes('w')) {
          rect.w = Math.max(minW, Math.min(g.start.x + g.start.w, g.start.w - dc))
          rect.x = g.start.x + g.start.w - rect.w
        }
        if (g.kind.includes('s')) rect.h = Math.max(minH, Math.min(maxH, g.start.h + dr))
        if (g.kind.includes('n')) rect.h = Math.max(minH, Math.min(maxH, g.start.h - dr))
        key = [rect.x, rect.w, rect.h].join(':')
        compute = () => resizeTo(g.snapshot, g.id, rect, WIDGETS_BY_KEY)
      }
      // Aperçu recalculé seulement quand la case visée change
      if (key !== g.key) {
        g.key = key
        g.preview = compute()
      }
    },

    onGestureEnd() {
      window.removeEventListener('pointermove', this.onGestureMove)
      window.removeEventListener('pointerup', this.onGestureEnd)
      window.removeEventListener('pointercancel', this.onGestureEnd)
      window.removeEventListener('scroll', this.applyGesture, true)
      if (this.autoScrollFrame) cancelAnimationFrame(this.autoScrollFrame)
      this.autoScrollFrame = null
      const g = this.gesture
      this.gesture = null
      if (!g) return
      // Simple clic : options du widget
      if (!g.moved) {
        if (g.kind === 'move') {
          const item = this.draft.layout.find(it => it.i === g.id)
          if (item) this.selectWidget(item)
        }
        return
      }
      if (g.preview) this.draft.layout = g.preview
      // Taille choisie à la main : plus d'ajustement automatique en attente
      if (g.kind !== 'move') this.fitTokens[g.id] = (this.fitTokens[g.id] || 0) + 1
    },

    // Case de la grille sous un point de l'écran
    pointerCell(point) {
      const grid = this.$refs.grid
      if (!grid || !point) return null
      const rect = grid.getBoundingClientRect()
      return {
        col: Math.max(0, Math.min(COLS - 1, Math.floor((point.x - rect.left - MARGIN / 2) / (this.colWidth + MARGIN)))),
        row: Math.max(0, Math.floor((point.y - rect.top - MARGIN / 2) / (ROW_HEIGHT + MARGIN)))
      }
    },

    onCatalogDragOver() {
      if (this.editing && this.catalogDragKey) this.dropActive = true
    },

    onCatalogDrop(event) {
      this.dropActive = false
      const key = this.catalogDragKey || (event.dataTransfer && event.dataTransfer.getData('text/plain'))
      this.catalogDragKey = null
      if (!this.editing || !WIDGETS_BY_KEY[key]) return
      this.addWidget(key, this.pointerCell({ x: event.clientX, y: event.clientY }))
    },

    onWindowResize() {
      this.narrow = window.innerWidth < NARROW
    },

    async save() {
      this.restoreNameIfEmpty()
      this.saving = true
      this.saveError = false
      const payload = {
        name: this.draft.name.trim(),
        visibility: this.draft.visibility,
        pages: this.draft.pages,
        period: this.draft.period,
        layout: this.draft.layout.map(({ i, module, x, y, w, h, options }) => ({ i, module, x, y, w, h, options: options || {} }))
      }
      try {
        const saved = this.isNew ? await this.createDashboard(payload) : await this.updateDashboard(this.dashboard.id, payload)
        if (!this.alive) return
        writeSection(this.cacheUserId(), `dashboard:${saved.slug}`, saved)
        this.fetchDashboards().catch(() => {})
        this.editing = false
        this.selectedId = null
        if (this.isNew) {
          this.$router.replace({ name: 'customDashboard', params: { id: saved.slug } })
          return
        }
        this.applyDashboard(saved)
        // URL lisible : suit le nouveau nom
        if (saved.slug && saved.slug !== this.dashboardId) this.$router.replace({ name: 'customDashboard', params: { id: saved.slug } })
      } catch (e) {
        this.saveError = true
      } finally {
        this.saving = false
      }
    },

    async duplicate() {
      if (!this.dashboard) return
      this.duplicating = true
      try {
        const copy = await this.duplicateDashboard(this.dashboard.id, this.$t('dash.overview.dashboards.copy_of', { name: this.dashboard.name }).slice(0, 60))
        this.fetchDashboards().catch(() => {})
        this.$router.push({ name: 'customDashboard', params: { id: copy.slug || copy.id } })
      } catch (e) {
        console.error('[dashboard] duplicate', e)
      } finally {
        this.duplicating = false
      }
    },

    confirmDelete() {
      EventBus.$emit('showModal', this.$t('dash.overview.dashboards.delete_title'), this.$t('dash.overview.dashboards.delete_text', { name: this.dashboard.name }),
        this.$t('dash.overview.dashboards.delete'), this.$t('dash.overview.dashboards.cancel'), () => this.remove(), () => {})
    },

    async remove() {
      try {
        await this.removeDashboard(this.dashboard.id)
        writeSection(this.cacheUserId(), `dashboard:${this.dashboardId}`, null)
        this.fetchDashboards().catch(() => {})
        this.goGeneral()
      } catch (e) {
        console.error('[dashboard] delete', e)
      }
    },

    goGeneral() {
      setLastDashboard(null)
      localStorage.setItem('section', 'overview')
      this.$router.push({ name: 'overview' })
    }
  }
}

</script>

<style src="../overview/overview.css"></style>

<style scoped>
.cd-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.cd-heading {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}
.cd-name-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}
.cd-name {
  margin: 0;
}
/* Barre d'édition : collée en haut, nom éditable en ligne */
.cd-editbar {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px 10px 16px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #f1edf5;
  border-radius: 14px;
  box-shadow: 0 6px 24px rgba(18, 24, 38, 0.06);
  backdrop-filter: blur(6px);
}
.cd-edit-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  padding: 3px 10px;
  border-radius: 20px;
  background: #f6f1fa;
  color: #6b1f4d;
  font-size: 11.5px;
  font-weight: 600;
}
.cd-edit-tag .material-icons {
  font-size: 14px;
}
.cd-name-input {
  flex: 1;
  min-width: 0;
  padding: 4px 8px;
  border: 1.5px solid transparent;
  border-radius: 8px;
  font-size: 20px;
  font-weight: 700;
  color: #121826;
  background: transparent;
  transition: border-color 0.15s, background 0.15s;
}
.cd-name-input:hover {
  background: #faf8fb;
}
.cd-name-input:focus {
  outline: none;
  border-color: #e0d0ea;
  background: #fff;
}
.cd-editbar .cd-actions {
  flex: 0 0 auto;
}
.section-overview.ov.ov button.ov-action--ghost {
  background: transparent;
  border-color: transparent;
  color: #555e75;
}
.section-overview.ov.ov button.ov-action--ghost:hover {
  background: #f6f1fa;
  color: #6b1f4d;
}
.cd-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: #f1edf5;
  color: #6b1f4d;
}
.cd-badge .material-icons {
  font-size: 15px;
}
.cd-badge--shared {
  background: #e6f9f0;
  color: #1a9e5e;
}
.cd-owner {
  font-size: 12.5px;
  color: #9ba3b7;
}
.cd-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.cd-error {
  font-size: 12.5px;
  color: #d93025;
}
.cd-period {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 18px;
  background: #fff;
  border: 1px solid #f1edf5;
  border-radius: 14px;
}
.cd-body {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}
.cd-grid-zone {
  flex: 1;
  min-width: 0;
  min-height: 300px;
  margin: 0 -16px;
  border-radius: 14px;
  transition: background 0.15s;
}
.cd--gesture .cd-grid-zone {
  background: repeating-linear-gradient(90deg, rgba(241, 237, 245, 0.55) 0, rgba(241, 237, 245, 0.55) calc(100% / 12 - 4px), transparent calc(100% / 12 - 4px), transparent calc(100% / 12));
}
.cd-grid-zone--drop {
  box-shadow: inset 0 0 0 1.5px #d3b6c6;
}
.cd-grid {
  position: relative;
}
.cd-item {
  position: absolute;
}
.cd-item--animate {
  transition: left 0.2s ease, top 0.2s ease, width 0.2s ease, height 0.2s ease;
}
/* Widget tiré : au premier plan, il suit le curseur */
.cd-item--active {
  z-index: 15;
  border-radius: 14px;
  box-shadow: 0 14px 36px rgba(18, 24, 38, 0.18);
}
.cd-placeholder {
  position: absolute;
  border: 1.5px dashed #b64985;
  border-radius: 14px;
  background: rgba(182, 73, 133, 0.08);
  transition: left 0.15s ease, top 0.15s ease, width 0.15s ease, height 0.15s ease;
}
.cd--gesture {
  user-select: none;
}
/* Pop-in ouverte (bloc « À traiter ») : le widget passe au-dessus des suivants et ne la coupe pas */
.cd-item:has(.alert-pop) {
  z-index: 20;
}
.cd-grid-zone >>> .mf:has(.alert-pop) > .mf-body,
.cd-grid-zone >>> .mf:has(.alert-pop) > .mf-body > * {
  overflow: visible;
}
.cd-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 280px;
  margin: 0 16px;
  border: 1.5px dashed #e0d0ea;
  border-radius: 14px;
  color: #9ba3b7;
  text-align: center;
}
.cd-empty .material-icons {
  font-size: 36px;
  color: #ab6e93;
}
.cd-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px;
}
.cd-loading {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.cd-loading-block {
  height: 160px;
  border-radius: 14px;
  background: #f1edf5;
}
</style>
